import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Building2, GraduationCap, Target, CheckCircle } from 'lucide-react';
import { partnershipImages } from '../data/portfolio';

const AboutPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % partnershipImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + partnershipImages.length) % partnershipImages.length);
  };

  return (
    <div className="bg-white transition-colors min-h-screen">
      {/* Hero Section with Slideshow */}
      <section className="bg-gradient-to-br from-blue-700 via-blue-600 to-blue-700 py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-center mb-12 text-blue-50">
            Tentang AIXpert
          </h1>

          {/* Partnership Slideshow */}
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Slideshow Image */}
              <div className="relative h-96 bg-gray-200">
                <img
                  src={partnershipImages[currentSlide].image}
                  alt={partnershipImages[currentSlide].title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect fill="%23e5e7eb" width="800" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="20" fill="%236b7280"%3E' + encodeURIComponent(partnershipImages[currentSlide].title) + '%3C/text%3E%3C/svg%3E';
                  }}
                />

                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-blue-700 p-3 rounded-full shadow-lg transition"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-blue-700 p-3 rounded-full shadow-lg transition"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>

              {/* Slideshow Caption */}
              <div className="p-6 bg-blue-50">
                <h3 className="text-xl font-bold text-blue-900 mb-2">
                  {partnershipImages[currentSlide].title}
                </h3>
                <p className="text-blue-700 mb-3">
                  {partnershipImages[currentSlide].description}
                </p>
                <p className="text-sm text-blue-600 font-semibold">
                  {partnershipImages[currentSlide].date}
                </p>
              </div>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 pb-4 bg-blue-50">
                {partnershipImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition ${
                      index === currentSlide ? 'bg-blue-700 w-8' : 'bg-blue-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* Bagaimana AIXpert Terbentuk */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-3 rounded-xl">
              <GraduationCap className="w-8 h-8 text-blue-700" />
            </div>
            <h2 className="text-3xl font-display font-bold text-gray-900">
              Bagaimana AIXpert Terbentuk?
            </h2>
          </div>
          <div className="bg-blue-50 rounded-2xl p-8 border-2 border-blue-200">
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              {/* EDIT: Ganti dengan cerita pembentukan yang sebenarnya */}
              AIXpert lahir dari visi VeloCT untuk memberikan kontribusi nyata dalam pengembangan talenta digital Indonesia. Berawal dari pengalaman kami dalam memberikan pelatihan data analytics untuk berbagai instansi pemerintah dan korporasi, kami melihat kebutuhan yang sangat besar akan pendidikan Data Science dan AI yang praktis dan aplikatif.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              {/* EDIT: Lanjutkan cerita */}
              Melalui kolaborasi strategis dengan Institut Sains & Teknologi Nasional (ISTN), kami membangun kurikulum yang menggabungkan keunggulan akademis dengan praktik industri. AIXpert hadir sebagai solusi pembelajaran yang tidak hanya mengajarkan teori, tetapi juga mempersiapkan siswa untuk langsung terjun ke dunia kerja.
            </p>
          </div>
        </section>

        {/* Visi Kami */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-3 rounded-xl">
              <Target className="w-8 h-8 text-blue-700" />
            </div>
            <h2 className="text-3xl font-display font-bold text-gray-900">Visi Kami</h2>
          </div>
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white shadow-xl">
            <p className="text-xl leading-relaxed">
              {/* EDIT: Ganti dengan visi yang sebenarnya */}
              Menjadi platform pembelajaran Data Science dan AI terpercaya yang membantu mengembangkan karier para pemula dan profesional di bidang teknologi data dan kecerdasan buatan, serta berkontribusi dalam transformasi digital Indonesia.
            </p>
          </div>
        </section>

        {/* Misi Kami */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-3 rounded-xl">
              <CheckCircle className="w-8 h-8 text-blue-700" />
            </div>
            <h2 className="text-3xl font-display font-bold text-gray-900">Misi Kami</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {/* EDIT: Tambah/kurangi/edit misi sesuai kebutuhan */}
            <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200 hover:border-blue-400 transition">
              <div className="flex items-start gap-4">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                  1
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Menyediakan kurikulum yang relevan dengan kebutuhan industri dan selalu diperbarui mengikuti perkembangan teknologi terkini
                </p>
              </div>
            </div>
            <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200 hover:border-blue-400 transition">
              <div className="flex items-start gap-4">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                  2
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Memberikan pengalaman belajar yang interaktif dan praktis melalui project-based learning dan mentoring langsung
                </p>
              </div>
            </div>
            <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200 hover:border-blue-400 transition">
              <div className="flex items-start gap-4">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                  3
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Membangun komunitas data science dan AI yang solid untuk networking dan kolaborasi profesional
                </p>
              </div>
            </div>
            <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200 hover:border-blue-400 transition">
              <div className="flex items-start gap-4">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                  4
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Mendukung transformasi digital Indonesia dengan mencetak talenta-talenta berkualitas di bidang Data & AI
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Profil VeloCT */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-3 rounded-xl">
              <Building2 className="w-8 h-8 text-blue-700" />
            </div>
            <h2 className="text-3xl font-display font-bold text-gray-900">Tentang VeloCT</h2>
          </div>
          <div className="bg-white rounded-2xl p-8 border-2 border-blue-200 shadow-lg">
            <div className="flex items-start gap-6 mb-6">
              <div className="bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 w-24 h-24 overflow-hidden border-2 border-gray-200">
                <img 
                  src="/images/mentors/logoveloct.png" 
                  alt="VeloCT Logo" 
                  className="w-20 h-20 object-contain"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%233b82f6" width="80" height="80"/%3E%3C/svg%3E';
                  }}
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-blue-900 mb-3">VeloCT</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {/* EDIT: Ganti dengan profil VeloCT yang sebenarnya */}
                  VeloCT atau PT Velo Citra Teknologi adalah perusahaan teknologi yang berfokus pada pengembangan solusi digital dan pendidikan teknologi. Didirikan pada tahun 2020, VeloCT telah dipercaya oleh berbagai instansi pemerintah dan perusahaan untuk memberikan pelatihan dan konsultasi di bidang Data Analytics, Data Science, dan Artificial Intelligence.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {/* EDIT: Tambahkan detail lainnya */}
                  Dengan pengalaman dalam menangani berbagai project training seperti pelatihan untuk Direktorat Jenderal Imigrasi Indonesia dan berbagai korporasi, VeloCT memiliki track record yang solid dalam mengembangkan talenta digital Indonesia.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Profil ISTN */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-3 rounded-xl">
              <GraduationCap className="w-8 h-8 text-blue-700" />
            </div>
            <h2 className="text-3xl font-display font-bold text-gray-900">
              Partnership dengan ISTN
            </h2>
          </div>
          <div className="bg-white rounded-2xl p-8 border-2 border-blue-200 shadow-lg">
            <div className="flex items-start gap-6 mb-6">
              <div className="bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 w-24 h-24 overflow-hidden border-2 border-gray-200">
                <img 
                  src="/images/mentors/logoistn.png" 
                  alt="ISTN Logo" 
                  className="w-20 h-20 object-contain"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%233b82f6" width="80" height="80"/%3E%3C/svg%3E';
                  }}
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-blue-900 mb-3">
                  Institut Sains & Teknologi Nasional
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {/* EDIT: Ganti dengan info ISTN yang sebenarnya */}
                  Institut Sains & Teknologi Nasional (ISTN) adalah perguruan tinggi terkemuka yang fokus pada pengembangan ilmu pengetahuan dan teknologi di Indonesia. Dengan komitmen untuk menghasilkan lulusan yang kompeten dan siap kerja, ISTN telah menjadi mitra strategis dalam pengembangan kurikulum AIXpert.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {/* EDIT: Info kerjasama */}
                  Kerjasama antara VeloCT dan ISTN menciptakan sinergi antara keunggulan akademis dan pengalaman industri praktis. Melalui partnership ini, AIXpert mendapatkan dukungan akademis yang kuat sekaligus akses ke sumber daya dan jaringan ISTN.
                </p>
              </div>
            </div>

            {/* Collaboration Highlights */}
            <div className="bg-blue-50 rounded-xl p-6 mt-6">
              <h4 className="font-bold text-blue-900 mb-4 text-lg">Bentuk Kerjasama:</h4>
              <ul className="space-y-3">
                {/* EDIT: Sesuaikan dengan bentuk kerjasama yang sebenarnya */}
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Pengembangan kurikulum bersama yang menggabungkan teori akademis dan praktik industri</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Sertifikasi program pembelajaran dari institusi pendidikan terakreditasi</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Akses ke fasilitas dan sumber daya pendidikan ISTN</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Program mentoring dan networking dengan akademisi dan praktisi industri</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mb-16">
          <h2 className="text-3xl font-display font-bold text-center mb-8 text-gray-900">
            Kenapa Memilih AIXpert?
          </h2>
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border-2 border-blue-200">
            <p className="text-gray-700 text-lg leading-relaxed text-center">
              Dengan metode pembelajaran berbasis project, mentor berpengalaman dari industri, kurikulum yang selalu diperbarui sesuai tren teknologi terkini, serta dukungan akademis dari ISTN dan pengalaman praktis VeloCT, AIXpert adalah pilihan tepat untuk memulai atau mengembangkan karier Anda di bidang Data Science dan Artificial Intelligence.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
};

export default AboutPage;
